import { DefaultTeamLogo, encode, EventIdentifier, StakingType, WithSkeleton } from '@capsicum/common';
import { ListCard, Reversible } from '@capsicum/common-web';
import { ChevronRightIcon, ChilizChainCircleIcon } from '@capsicum/common-web/icons';
import { useFanTokenStakingAnalytics } from '@capsicum/socios-core';
import { cloneElement } from 'react';
import { useTheme } from 'styled-components';

import { NCWalletTokenListCardSkeleton } from './NCWalletTokenListCardSkeleton';
import * as S from './styled';

export type NCWalletTokenListCardProps = {
	captionLeftIcon?: JSX.Element;
	captionLeftText?: string;
	currencyId?: string;
	formattedAltBalance?: string;
	formattedDefaultBalance?: string;
	isExternalToken?: boolean;
	isPrimaryButtonLoading?: boolean;
	onBodyClick?: () => void;
	onIconClick?: () => void;
	onPrimaryButtonClick?: () => void;
	partnerId?: string;
	primaryButtonLabel?: string;
	showChzBalance?: boolean;
	testID?: string;
	title?: string;
	tokenBalance?: string;
	tokenImgBlurhash?: string;
	tokenImgUrl?: string;
	usableFormattedDefaultBalance?: string;
	usableTokenBalance?: string;
	variant?: string;
};

const NCWalletTokenListCardComponent = ({
	captionLeftIcon,
	captionLeftText,
	currencyId,
	formattedAltBalance,
	formattedDefaultBalance,
	isPrimaryButtonLoading,
	onBodyClick,
	onIconClick,
	onPrimaryButtonClick,
	partnerId,
	primaryButtonLabel,
	showChzBalance = true,
	testID,
	title,
	tokenBalance,
	tokenImgBlurhash,
	tokenImgUrl,
	usableTokenBalance,
	variant
}: NCWalletTokenListCardProps) => {
	const { colors } = useTheme();

	const { sendFanTokenStakingEvent } = useFanTokenStakingAnalytics({
		amount: parseInt(usableTokenBalance || ''),
		currencyId: currencyId,
		currentPage: 'wallet_page',
		eventIdentifier: EventIdentifier.TriggerClickFtStaking,
		partnerId: partnerId,
		transactionType: StakingType.STAKE
	});

	const primaryButtonVisible = !!onPrimaryButtonClick && !!primaryButtonLabel;

	const CaptionLeftIconWithProps =
		captionLeftIcon &&
		cloneElement(captionLeftIcon, {
			color: colors.icons.main.enabled,
			height: '12',
			width: '12'
		});

	return (
		<S.ListCardWrapper testID={testID ? testID : `ncWallet-token-listCard-${testID}`}>
			<ListCard.Start>
				<div>
					{variant === 'token' ? (
						<ListCard.Image
							blurhash={tokenImgBlurhash}
							optimized
							src={tokenImgUrl || DefaultTeamLogo}
							testID={`ncWallet-token-partnerLogo-${testID}`}
							onClick={onIconClick}
						/>
					) : (
						<ChilizChainCircleIcon height={48} width={48} onClick={onIconClick} />
					)}
				</div>
			</ListCard.Start>
			<S.ContentWrapper $clickable={onBodyClick ? true : false} onClick={onBodyClick}>
				<S.ListCardContent>
					<S.FirstRow>
						<S.ListCardTitle data-testid={`ncWallet-tokens-cardTeamName-${testID}`}>
							{title}
						</S.ListCardTitle>
						<S.RightFirstRowWrapper>
							<S.ListCardText testID={`ncWallet-tokens-cardPartnerBalance-${testID}`}>
								{variant === 'token' ? tokenBalance : formattedDefaultBalance}
							</S.ListCardText>
							<S.CurrencyContainer>
								<S.DefaultCurrencyContainer>
									{variant === 'token' && showChzBalance && formattedDefaultBalance}
								</S.DefaultCurrencyContainer>
								{formattedAltBalance && (
									<S.AltCurrencyContainer>
										{encode('doubleTilde')}
										{formattedAltBalance}
									</S.AltCurrencyContainer>
								)}
							</S.CurrencyContainer>
						</S.RightFirstRowWrapper>
					</S.FirstRow>
					{(captionLeftText || primaryButtonVisible) && (
						<>
							<ListCard.ContentDivider />
							<ListCard.Row>
								{captionLeftText && (
									<S.ListCardBottomTextWrapper>
										{captionLeftIcon && CaptionLeftIconWithProps}
										<S.ListCardBottomText data-testid={`ncWallet-tokens-cardStaked-${testID}`}>
											{captionLeftText}
										</S.ListCardBottomText>
									</S.ListCardBottomTextWrapper>
								)}

								{primaryButtonVisible && (
									<S.PrimaryButtonWrapper>
										<S.PrimaryButton
											color="brand"
											isLoading={isPrimaryButtonLoading}
											size="xsmall"
											testID={`${testID}-primaryButton`}
											text={primaryButtonLabel}
											textSize="xsmall"
											onClick={e => {
												e.stopPropagation();
												sendFanTokenStakingEvent();
												onPrimaryButtonClick();
											}}
										/>
									</S.PrimaryButtonWrapper>
								)}
							</ListCard.Row>
						</>
					)}
				</S.ListCardContent>
				{!!onBodyClick && (
					<S.ListCardEndTop>
						<Reversible>
							<ChevronRightIcon color={colors.text.main.enabled} height={24} width={24} />
						</Reversible>
					</S.ListCardEndTop>
				)}
			</S.ContentWrapper>
		</S.ListCardWrapper>
	);
};

export const NCWalletTokenListCard = WithSkeleton(NCWalletTokenListCardComponent, NCWalletTokenListCardSkeleton);
